import React, { useCallback, useMemo } from "react";
import { useState, useEffect } from "react";
import styles from "./CPNWReviewerPage.module.css";
import { CpnwContainer } from "hooks/useCpnw";
import { HeaderSection, LinkItem, Loading } from "@cpnw/ui";
import RequirementTable from "components/RequirementTable";
import { AdminContainer } from "hooks/useAdmin";
import { format, parseISO } from "date-fns";
import { StaticRoutes } from "pages/routes";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import PassportAdminReviewerTable from "components/PassportAdminReviewerTable/PassportAdminReviewerTable";
import PassportRequirementSubmissionsReviewer from "components/PassportRequirementSubmissionsReviewer/PassportRequirementSubmissionsReviewer";
import { RequirementContainer } from "hooks/useRequirement";
import { AccountContainer } from "hooks/useAccount";

export default function CPWNReviewPage() {
  const history = useHistory();
  const {
    contactsToReviewData,
    refetchContactsToReviewData,
    postVerificationComplete,
  } = RequirementContainer.useContainer();

  const { userRoles } = AccountContainer.useContainer();

  // API Call on mount
  useEffect(async () => {
    setIsLoading(true);
    await refetchContactsToReviewData();
    setIsLoading(false);
  }, []);

  const data = useMemo(() => {
    if (contactsToReviewData && contactsToReviewData.data.length > 0) {

      const reviews = contactsToReviewData.data.flatMap((a) => {
        return {
          id: a.ContactId,
          name: `${a.NameLast}, ${a.NameFirst}`,
          sid: a.IndividualId || "",
          school: a.Facility,
          program: a.Program,
          status: a.RegistrationStatus,
          reviewStatus: a.reviewStatus ? "Needs Review" : null,
          oldestSubmittedOn: a.OldestSubmittedOn || null,
          VerificationComplete: a.VerificationComplete,
          VerifiedByType: a.VerifiedByType,
        };
      });

      const needsReviewAccounts = reviews.filter(reviews => reviews.reviewStatus === "Needs Review")
      needsReviewAccounts.sort((a, b) => {
        const dateDiff = new Date(a.oldestSubmittedOn) - new Date(b.oldestSubmittedOn);
        if (dateDiff !== 0) return dateDiff;
        return a.name.localeCompare(b.name);
      });

      const blankStatusAccounts = reviews.filter(reviews => reviews.reviewStatus === null);
      blankStatusAccounts.sort((a, b) => a.name.localeCompare(b.name));

      const sortedAccounts = [...needsReviewAccounts, ...blankStatusAccounts];

      return sortedAccounts;
    }
    return [];
  }, [contactsToReviewData]);

  const [submissionModalOpen, setSubmissionModalOpen] = useState(false);
  const [submissionModalData, setSubmissionModalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const roles = userRoles.map((a) => a.toUpperCase());

  const onItemView = useCallback(
    (item) => {
      const student = contactsToReviewData.data.find(
        (a) => a.ContactId == item.id
      );

      const modalData = {
        contactId: student.ContactId,
        studentInfo: {
          lastName: student.NameLast,
          firstName: student.NameFirst,
          previousNames: student.PreviousNames,
          school: student.Facility,
          program: student.Program,
          sidEid: student.IndividualId || "",
          dob: student.DOB,
          email: student.Email,
          phone: student.PrimaryPhone,
          eContact: student.EmergencyContact,
          eContactPhone: student.EmergencyPhone,
        },
        studentPhoto: student.PhotoUrl,
        assignmentsInfo: student.Assignments.flatMap((a) => ({
          location: a.Facility,
          startDate: a.StartDate,
          endDate: a.EndDate,
          status: a.Status
        })),
        recordInfo: [],
      };

      setSubmissionModalData(modalData);
      setSubmissionModalOpen(true);
    },
    [contactsToReviewData]
  );

  return (
    <div className={styles.container}>
      {submissionModalOpen && (
        <PassportRequirementSubmissionsReviewer
          onModalClose={() => {
            setSubmissionModalData(null);
            setSubmissionModalOpen(false);
          }}
          canCompleteVerification={roles.includes("EDUCATION") || roles.includes("FACULTY") || roles.includes("CPNW-REVIEWER")}
          onCompleteVerification={() => {
            postVerificationComplete(submissionModalData.contactId).then(() => {
              refetchContactsToReviewData();
            });
          }}
          assignmentsInfo={submissionModalData.assignmentsInfo}
          recordInfo={submissionModalData.recordInfo}
          studentInfo={submissionModalData.studentInfo}
          studentPhoto={submissionModalData.studentPhoto}
          contactId={submissionModalData.contactId}
        />
      )}
      <PassportAdminReviewerTable items={data} onItemView={onItemView} />
      {isLoading && (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}
    </div>
  );
}
